import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useApplicantContext } from '../contexts/ApplicantContext';
import { Card, Badge, Button, Label, Select, Input } from '../components/ui';
import { PageHeader } from '../components/PageHeader';
import { Trash2, Download, FileUp, Link2, ExternalLink, Pencil, Check, X } from 'lucide-react';
import { format } from 'date-fns';
import { humanize } from '../lib/humanize';
import type { DocumentRecord, DocumentType, DocumentKind } from '@scholarship-app/types';

// =============================================================================
// DocumentsPage (v0.2.30)
//
// Documents now holds two kinds of entry:
//   - 'file': an uploaded file in Firebase Storage (transcripts, score
//     reports, etc.) — the original behavior.
//   - 'link': an external URL, e.g. a Google Doc. This is how essays are
//     tracked — the app does not store essay text; Mia keeps writing in
//     Google Docs / Word and the app holds a link plus notes.
//
// Essays ('essay' document type) also have a free-text "used for" field to
// record which scholarships an essay has been used for or adapted toward —
// the lightweight substitute for a full version-history system.
//
// The page groups entries into "Essays & Links" and "Files" so it serves both
// jobs without a separate tab.
// =============================================================================

const DOC_TYPES: DocumentType[] = [
  'parent_transcript',
  'parent_course_descriptions',
  'parent_reading_list',
  'community_college_transcript',
  'letter_of_rec',
  'resume',
  'headshot',
  'act_score_report',
  'sat_score_report',
  'art_portfolio',
  'essay',
  'other',
];

function genId(prefix: string) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

/** Treat a record with no `kind` (pre-v0.2.30) as a file. */
function recordKind(d: DocumentRecord): DocumentKind {
  return d.kind ?? 'file';
}

/** Light URL check / normalization for link entries. */
function normalizeUrl(raw: string): string | null {
  let s = raw.trim();
  if (!s) return null;
  if (!/^https?:\/\//i.test(s)) s = 'https://' + s;
  try {
    const u = new URL(s);
    if (!u.hostname.includes('.')) return null;
    return u.toString();
  } catch {
    return null;
  }
}

function uploadedDate(d: DocumentRecord): Date | null {
  const ts = d.uploaded_at as unknown;
  if (ts && typeof ts === 'object' && 'toDate' in (ts as object) &&
      typeof (ts as { toDate: unknown }).toDate === 'function') {
    return (ts as { toDate: () => Date }).toDate();
  }
  if (typeof ts === 'string') {
    const dt = new Date(ts);
    return isNaN(dt.getTime()) ? null : dt;
  }
  return null;
}

export function DocumentsPage() {
  const { user, membership } = useAuth();
  const { selectedApplicantId } = useApplicantContext();
  const applicantId = selectedApplicantId;
  const [docs, setDocs] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Add form
  const [entryKind, setEntryKind] = useState<DocumentKind>('file');
  const [uploading, setUploading] = useState(false);
  const [pendingType, setPendingType] = useState<DocumentType>('parent_transcript');
  const [pendingTitle, setPendingTitle] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingUrl, setPendingUrl] = useState('');
  const [pendingUsedFor, setPendingUsedFor] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Inline "used for" editing
  const [editingUsedForId, setEditingUsedForId] = useState<string | null>(null);
  const [usedForDraft, setUsedForDraft] = useState('');

  useEffect(() => {
    if (!db || !membership || !applicantId) return;
    const unsub = onSnapshot(
      collection(db, 'households', membership.householdId, 'applicants', applicantId, 'documents'),
      (snap) => {
        setDocs(snap.docs.map((d) => d.data() as DocumentRecord));
        setLoading(false);
      }
    );
    return () => unsub();
  }, [membership, applicantId]);

  // When the user picks the "essay" type, default the entry kind to a link
  // (essays are Google Docs), which is the common case.
  useEffect(() => {
    if (pendingType === 'essay') setEntryKind('link');
  }, [pendingType]);

  const { essaysAndLinks, files } = useMemo(() => {
    const active = docs.filter((d) => !d.archived);
    return {
      essaysAndLinks: active.filter((d) => recordKind(d) === 'link'),
      files: active.filter((d) => recordKind(d) === 'file'),
    };
  }, [docs]);

  async function handleAdd() {
    if (!db || !user || !membership || !applicantId) return;
    setFormError(null);

    if (entryKind === 'file') {
      if (!storage || !pendingFile) {
        setFormError('Choose a file to upload.');
        return;
      }
    } else {
      const normalized = normalizeUrl(pendingUrl);
      if (!normalized) {
        setFormError('Enter a valid link (e.g. a Google Doc URL).');
        return;
      }
    }

    setUploading(true);
    try {
      const docId = genId('doc');
      let storagePath = '';
      let mimeType = '';
      let sizeBytes = 0;
      let linkUrl = '';

      if (entryKind === 'file' && pendingFile && storage) {
        const ext = pendingFile.name.split('.').pop();
        storagePath = `households/${membership.householdId}/applicants/${applicantId}/documents/${docId}.${ext}`;
        await uploadBytes(storageRef(storage, storagePath), pendingFile);
        mimeType = pendingFile.type;
        sizeBytes = pendingFile.size;
      } else {
        linkUrl = normalizeUrl(pendingUrl) ?? '';
      }

      const title =
        pendingTitle.trim() ||
        (entryKind === 'file' ? pendingFile?.name ?? 'Untitled' : 'Untitled link');

      await setDoc(
        doc(db, 'households', membership.householdId, 'applicants', applicantId, 'documents', docId),
        {
          document_id: docId,
          document_type: pendingType,
          kind: entryKind,
          title,
          storage_path: storagePath,
          link_url: linkUrl,
          mime_type: mimeType,
          size_bytes: sizeBytes,
          uploaded_at: serverTimestamp(),
          uploaded_by: user.uid,
          expiry_date: null,
          signed_at: null,
          notarized: false,
          notarized_at: null,
          notes: '',
          used_for: pendingType === 'essay' ? pendingUsedFor.trim() : '',
          archived: false,
        }
      );

      setPendingFile(null);
      setPendingTitle('');
      setPendingUrl('');
      setPendingUsedFor('');
    } catch (e) {
      setFormError('Could not save: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setUploading(false);
    }
  }

  async function handleDownload(d: DocumentRecord) {
    if (!storage) return;
    const url = await getDownloadURL(storageRef(storage, d.storage_path));
    window.open(url, '_blank');
  }

  async function handleDelete(d: DocumentRecord) {
    if (!db || !membership || !applicantId) return;
    if (!confirm(`Delete "${d.title}"?`)) return;
    try {
      // Only file entries have something in Storage to remove.
      if (recordKind(d) === 'file' && storage && d.storage_path) {
        await deleteObject(storageRef(storage, d.storage_path)).catch(() => {});
      }
      await deleteDoc(
        doc(db, 'households', membership.householdId, 'applicants', applicantId, 'documents', d.document_id)
      );
    } catch (e) {
      alert('Delete failed: ' + (e instanceof Error ? e.message : String(e)));
    }
  }

  function startEditUsedFor(d: DocumentRecord) {
    setEditingUsedForId(d.document_id);
    setUsedForDraft(d.used_for ?? '');
  }

  async function saveUsedFor(d: DocumentRecord) {
    if (!db || !membership || !applicantId) return;
    try {
      await updateDoc(
        doc(db, 'households', membership.householdId, 'applicants', applicantId, 'documents', d.document_id),
        { used_for: usedForDraft.trim() }
      );
      setEditingUsedForId(null);
    } catch (e) {
      alert('Save failed: ' + (e instanceof Error ? e.message : String(e)));
    }
  }

  function onFileChange(e: ChangeEvent<HTMLInputElement>) {
    setPendingFile(e.target.files?.[0] ?? null);
  }

  if (loading) return <div className="text-soft">Loading documents…</div>;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Documents"
        description="Transcripts, score reports, recommendation letters — and links to essays."
      />

      {/* ===================== Add form ===================== */}
      <Card className="p-4 space-y-3">
        <h2 className="font-medium">Add a document or essay link</h2>

        {/* File vs. link toggle */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setEntryKind('file')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border ${
              entryKind === 'file'
                ? 'border-forest-300 bg-forest-50 text-forest-800'
                : 'border-line text-soft'
            }`}
          >
            <FileUp size={14} />Upload a file
          </button>
          <button
            type="button"
            onClick={() => setEntryKind('link')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border ${
              entryKind === 'link'
                ? 'border-forest-300 bg-forest-50 text-forest-800'
                : 'border-line text-soft'
            }`}
          >
            <Link2 size={14} />Add a link
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <Label htmlFor="doc_type">Type</Label>
            <Select id="doc_type" value={pendingType} onChange={(e) => setPendingType(e.target.value as DocumentType)}>
              {DOC_TYPES.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}
            </Select>
          </div>
          <div>
            <Label htmlFor="doc_title">Title (optional)</Label>
            <Input
              id="doc_title"
              type="text"
              value={pendingTitle}
              onChange={(e) => setPendingTitle(e.target.value)}
              placeholder={pendingType === 'essay' ? 'e.g. Art therapy "why" essay' : 'e.g. Q2 2026 transcript'}
            />
          </div>
          {entryKind === 'file' ? (
            <div>
              <Label htmlFor="doc_file">File</Label>
              <input id="doc_file" type="file" onChange={onFileChange} className="w-full text-sm" />
            </div>
          ) : (
            <div>
              <Label htmlFor="doc_url">Link</Label>
              <Input
                id="doc_url"
                type="url"
                inputMode="url"
                value={pendingUrl}
                onChange={(e) => setPendingUrl(e.target.value)}
                placeholder="Paste a Google Doc link"
              />
            </div>
          )}
        </div>

        {/* Essay-only: "used for" */}
        {pendingType === 'essay' && (
          <div>
            <Label htmlFor="doc_usedfor">Used for (optional)</Label>
            <Input
              id="doc_usedfor"
              type="text"
              value={pendingUsedFor}
              onChange={(e) => setPendingUsedFor(e.target.value)}
              placeholder="Which scholarships has this essay been used for or adapted toward?"
            />
          </div>
        )}

        {formError && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
            {formError}
          </div>
        )}

        <Button onClick={handleAdd} disabled={uploading}>
          {entryKind === 'file' ? <FileUp size={16} className="mr-1" /> : <Link2 size={16} className="mr-1" />}
          {uploading ? 'Saving…' : entryKind === 'file' ? 'Upload' : 'Add link'}
        </Button>
      </Card>

      {/* ===================== Essays & links ===================== */}
      <div className="space-y-2">
        <h2 className="font-medium text-ink">Essays &amp; Links</h2>
        {essaysAndLinks.length === 0 ? (
          <Card className="p-6 text-center text-soft text-sm">
            No essay links yet. Use "Add a link" above to track a Google Doc essay.
          </Card>
        ) : (
          essaysAndLinks.map((d) => {
            const isEditing = editingUsedForId === d.document_id;
            return (
              <Card key={d.document_id} className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-ink">{d.title}</span>
                      <Badge variant={d.document_type === 'essay' ? 'indigo' : 'gray'}>
                        {humanize(d.document_type)}
                      </Badge>
                    </div>
                    {d.link_url && (
                      <a
                        href={d.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-forest-700 hover:underline flex items-center gap-1 mt-1 break-all"
                      >
                        <ExternalLink size={12} className="shrink-0" />
                        {d.link_url}
                      </a>
                    )}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(d)}>
                    <Trash2 size={12} className="mr-1" />Delete
                  </Button>
                </div>

                {/* Essay "used for" */}
                {d.document_type === 'essay' && (
                  <div className="border-t border-line pt-2">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={usedForDraft}
                          onChange={(e) => setUsedForDraft(e.target.value)}
                          placeholder="Which scholarships has this been used for?"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveUsedFor(d);
                            if (e.key === 'Escape') setEditingUsedForId(null);
                          }}
                          autoFocus
                        />
                        <Button size="sm" onClick={() => saveUsedFor(d)}>
                          <Check size={12} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingUsedForId(null)}>
                          <X size={12} />
                        </Button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startEditUsedFor(d)}
                        className="text-left w-full group"
                      >
                        <span className="text-xs font-medium text-soft">Used for: </span>
                        <span className="text-xs text-soft">
                          {d.used_for?.trim() || <span className="text-soft italic">not set — tap to add</span>}
                        </span>
                        <Pencil size={11} className="inline ml-1 text-line group-hover:text-soft" />
                      </button>
                    )}
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>

      {/* ===================== Files ===================== */}
      <div className="space-y-2">
        <h2 className="font-medium text-ink">Files</h2>
        {files.length === 0 ? (
          <Card className="p-6 text-center text-soft text-sm">No files uploaded yet.</Card>
        ) : (
          files.map((d) => {
            const date = uploadedDate(d);
            return (
              <Card key={d.document_id} className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-ink">{d.title}</span>
                    <Badge variant="gray">{humanize(d.document_type)}</Badge>
                  </div>
                  <p className="text-xs text-soft mt-1">
                    {(d.size_bytes / 1024).toFixed(0)} KB
                    {date ? ` • uploaded ${format(date, 'MMM d, yyyy')}` : ''}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => handleDownload(d)}>
                    <Download size={12} className="mr-1" />Download
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(d)}>
                    <Trash2 size={12} className="mr-1" />Delete
                  </Button>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
